import { createFileRoute } from '@tanstack/react-router'
import { useOrders, useEvent } from '@/lib/api/queries'
import { apiClient } from '@/lib/api/client'
import { useAuth } from '@/lib/auth'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Download } from 'lucide-react'
import { useState, useMemo } from 'react'
import { DateFilter } from '@/components/ui/date-filter'
import { DEFAULT_EVENT_TIMEZONE, eventLocalToServerNaive } from '@/lib/eventTime'
import { cn } from '@/lib/utils'
import { useAutoRefresh } from '@/lib/hooks/use-auto-refresh'
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value'
import { formatDate } from '@/lib/date'
import { useModal } from '@/components/modals/modal-container'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'

export const Route = createFileRoute('/_authenticated/events/$eventId/orders')({
  component: OrdersPage,
})

type OrderStatus = 'all' | 'completed' | 'in_progress' | 'ready' | 'delivered' | 'cancelled'

const statusTabs: { key: OrderStatus; label: string }[] = [
  { key: 'all', label: 'All Orders' },
  { key: 'completed', label: 'Completed' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'ready', label: 'Ready' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'cancelled', label: 'Cancelled' },
]

// The Order model has no 'ready'/'in_progress' statuses — tabs map onto the
// real pipeline (pre-ordered/ordered → accepted → completed → delivered):
// In Progress = open work, Ready = prepared awaiting pickup (completed),
// Completed = fulfilled either way, Cancelled includes vendor rejections.
const statusFilters: Record<OrderStatus, { status?: string; status__in?: string } | undefined> = {
  all: undefined,
  completed: { status__in: 'completed,delivered' },
  in_progress: { status__in: 'pre-ordered,ordered,accepted' },
  ready: { status: 'completed' },
  delivered: { status: 'delivered' },
  cancelled: { status__in: 'cancelled,rejected' },
}

function OrdersPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const { openUserModal, openOrderModal } = useModal()
  const { token } = useAuth()
  
  // Permission checks
  const canViewOrders = usePermission('can_view_orders')
  const canSeeUserInfo = usePermission('can_see_user_info')
  const canExportOrders = usePermission('can_export_orders')
  
  // All hooks must be called before any early returns
  const [activeStatus, setActiveStatus] = useState<OrderStatus>('all')
  const [isExporting, setIsExporting] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput, 400)
  const [page, setPage] = useState(1)
  const [ordering, setOrdering] = useState('-created_at')
  const [pageSize, setPageSize] = useState(20)
  const pageSizeOptions = [20, 50, 100, 200]
  const [dateRange, setDateRange] = useState<{ gte?: string; lte?: string }>({})

  const { data: event } = useEvent(eventIdNum)

  // Event-local range -> server-naive for the list (created_at gte/lte);
  // date-only start/end for the export endpoint.
  const dateParams = useMemo(() => {
    const tz = event?.timezone || DEFAULT_EVENT_TIMEZONE
    const p: { created_at__gte?: string; created_at__lte?: string } = {}
    if (dateRange.gte) p.created_at__gte = eventLocalToServerNaive(dateRange.gte, tz)
    if (dateRange.lte) p.created_at__lte = eventLocalToServerNaive(dateRange.lte, tz)
    return p
  }, [dateRange, event?.timezone])
  const exportDateParams = useMemo(() => {
    // Date-only export bounds: use the event-local CALENDAR date the user
    // picked (slice 'yyyy-MM-dd' off the event-local value). Converting the
    // 23:59 end-of-day through the server tz rolls the date forward a day
    // for events west of the server, widening the exported range.
    const p: { start_date?: string; end_date?: string } = {}
    if (dateRange.gte) p.start_date = dateRange.gte.slice(0, 10)
    if (dateRange.lte) p.end_date = dateRange.lte.slice(0, 10)
    return p
  }, [dateRange])

  // Auto-refresh from header toggle
  const { refetchInterval } = useAutoRefresh()

  const { data, isLoading, error } = useOrders(eventIdNum, {
    limit: pageSize,
    offset: (page - 1) * pageSize,
    search: debouncedSearch || undefined,
    ...statusFilters[activeStatus],
    ...dateParams,
    ordering,
  }, { refetchInterval })

  // Permission guard - now safe after all hooks
  if (!canViewOrders) {
    return <AccessDenied message="You don't have permission to view orders." />
  }

  const orders = data?.results || []
  const totalCount = data?.count || 0
  const totalPages = Math.ceil(totalCount / pageSize)
  const tokenValue = event?.token_value || 0
  const currency = event?.currency || 'USD'

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency,
    }).format(value)
  }

  const handleStatusChange = (status: OrderStatus) => {
    setActiveStatus(status)
    setPage(1)
  }

  const handleOrderingChange = (field: string) => {
    if (ordering === field) {
      setOrdering(`-${field}`)
    } else if (ordering === `-${field}`) {
      setOrdering(field)
    } else {
      setOrdering(`-${field}`)
    }
  }

  const getOrderingIcon = (field: string) => {
    if (ordering === field) {
      return <ChevronUp className="w-4 h-4 inline ml-1" />
    } else if (ordering === `-${field}`) {
      return <ChevronDown className="w-4 h-4 inline ml-1" />
    }
    return null
  }

  const getStatusBadge = (status: string) => {
    // Real Order.status values (there is no 'ready'/'in_progress' status)
    const statusClasses: Record<string, string> = {
      'pre-ordered': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
      ordered: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
      accepted: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      delivered: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      refunded: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
      partial: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
    }
    const className =
      statusClasses[status] || 'bg-neutral-100 text-neutral-800 dark:bg-neutral-900/20 dark:text-neutral-400'
    const label = status === 'partial' ? 'Partially refunded' : status.replace(/[_-]/g, ' ')
    const displayText = label.charAt(0).toUpperCase() + label.slice(1)

    return (
      <span className={cn('px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full', className)}>
        {displayText}
      </span>
    )
  }

  const handleExport = async () => {
    if (!token) return
    setIsExporting(true)
    try {
      await apiClient.exportOrders(token, eventIdNum, {
        search: debouncedSearch || undefined,
        ...statusFilters[activeStatus],
        ...exportDateParams,
        ordering,
      })
    } catch (err) {
      console.error('Export failed:', err)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8 py-8">
      {/* Header */}
      <div className="flex flex-row justify-between items-center mb-3 sm:mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">Orders</h1>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-neutral-200 dark:border-neutral-800 overflow-x-auto">
        <div className="flex space-x-6 min-w-max">
          {statusTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleStatusChange(tab.key)}
              className={cn(
                'px-1 py-3 text-sm font-medium whitespace-nowrap',
                activeStatus === tab.key
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-500'
                  : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        {/* Search Bar */}
        <div className="flex flex-row sm:flex-row gap-4 items-start sm:items-center">
          <div className="relative w-full sm:grow">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
            </div>
            <Input
              type="text"
              placeholder="Search by Order, Vendor, Customer or Date"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value)
                setPage(1)
              }}
              className="bg-white dark:bg-[#131313] w-full pl-10 pr-3 py-2 border border-neutral-200 dark:border-neutral-800 rounded-md text-sm text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <DateFilter
            value={dateRange}
            onChange={(v) => { setDateRange(v); setPage(1) }}
            eventTimeZone={event?.timezone}
            defaultMonth={event?.start_date_time ? new Date(event.start_date_time) : undefined}
            label="Date range"
            className="shrink-0"
          />

          {/* Export Button - only show if user has permission */}
          {canExportOrders && (
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={isExporting}
              className="inline-flex items-center px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md shadow-sm text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-700"
            >
              <Download className="w-4 h-4 mr-2" />
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          )}
        </div>

        {/* Table Container */}
        <div className="w-full overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden border border-neutral-200 dark:border-neutral-800 sm:rounded-lg">
              <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
                <thead className="bg-neutral-50 dark:bg-neutral-900 sticky top-0 z-10">
                  <tr>
                    <th
                      scope="col"
                      onClick={() => handleOrderingChange('id')}
                      className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    >
                      Order ID {getOrderingIcon('id')}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"
                    >
                      Vendor
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"
                    >
                      Amount
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"
                    >
                      Location
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"
                    >
                      Customer
                    </th>
                    <th
                      scope="col"
                      onClick={() => handleOrderingChange('status')}
                      className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    >
                      Status {getOrderingIcon('status')}
                    </th>
                    <th
                      scope="col"
                      onClick={() => handleOrderingChange('created_at')}
                      className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    >
                      Date & Time {getOrderingIcon('created_at')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
                  {isLoading ? (
                    // Loading skeleton
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        {Array.from({ length: 7 }).map((_, j) => (
                          <td key={j} className="px-6 py-4">
                            <div className="h-4 w-16 bg-neutral-200 dark:bg-neutral-800 rounded" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : error ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-red-500">
                        Failed to load orders: {error.message}
                      </td>
                    </tr>
                  ) : orders.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-neutral-500 dark:text-neutral-400">
                        No orders found
                      </td>
                    </tr>
                  ) : (
                    orders.map((order) => {
                      const vendorEventObj = typeof order.vendor_event === 'object' ? order.vendor_event : null
                      const userObj = typeof order.user === 'object' ? order.user : null
                      return (
                        <tr key={order.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-900">
                          <td className="px-6 py-2 text-sm font-medium">
                            <button
                              onClick={() => openOrderModal(order.id, eventIdNum)}
                              className="text-blue-600 dark:text-blue-500 hover:underline"
                            >
                              {order.order_display_id || order.id}
                            </button>
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                            {vendorEventObj?.vendor?.name || 'Unknown Vendor'}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                            {(() => {
                              const tokenTotal = order.order_total ?? order.total ?? 0
                              const roundedTokens = Math.round(tokenTotal)
                              const cashTotal = tokenValue ? roundedTokens * tokenValue : 0
                              return tokenValue
                                ? `${roundedTokens} Tokens (${formatCurrency(cashTotal)})`
                                : `${roundedTokens} Tokens`
                            })()}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                            {vendorEventObj?.booth || '-'}
                          </td>
                          <td className="px-6 py-2 text-sm text-neutral-500 dark:text-neutral-400">
                            {userObj ? (
                              canSeeUserInfo ? (
                                <button
                                  onClick={() => openUserModal(userObj.id, eventIdNum)}
                                  className="text-blue-600 dark:text-blue-500 hover:underline"
                                >
                                  {userObj.first_name} {userObj.last_name}
                                </button>
                              ) : (
                                <span>{userObj.first_name}{userObj.last_name ? ` ${userObj.last_name.charAt(0)}.` : ''}</span>
                              )
                            ) : (
                              'Unknown'
                            )}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm">{getStatusBadge(order.status)}</td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                            {formatDate(order.created_at || order.created, 'MMM d, yyyy h:mm a')}
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="bg-white dark:bg-[#131313] px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-neutral-200 dark:border-neutral-800 sm:px-6 gap-3">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Showing <span className="font-medium">{orders.length}</span> of{' '}
                    <span className="font-medium">{totalCount.toLocaleString()}</span> orders
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-neutral-500 dark:text-neutral-400">Per page:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value))
                        setPage(1)
                      }}
                      className="text-sm border border-neutral-200 dark:border-neutral-700 rounded-md px-2 py-1 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {pageSizeOptions.map((size) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center flex-wrap gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Prev
                    </Button>
                    <span className="px-3 py-1 text-sm text-neutral-600 dark:text-neutral-400">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
